import React from 'react';
import { Box, Grid, Link, Typography, useMediaQuery } from '@mui/material';
import { Email, Phone } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountData, HolidayCalendar, Language } from '../../api';
import { useLanguage } from '../../utils/GeneralUtils';
import { AccountLogo } from '../../shared/ui/AccountLogo';
import { LanguageSwitcher } from '../../shared/ui/LanguageSwitcher';
import { whatsAppLinkFormatter } from '../../storefront/utils';

type Props = {
  account: AccountData;
};

/**
 * What a fixed-schedule member sees instead of the portal. Their sessions are booked for them, so
 * there is nothing here for them to act on -- the server returns them no passes at all. Without
 * this screen the dashboard has no pass to render from and the member lands on a blank page.
 */
export const StudioManagedMembership = ({ account }: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  const contact = account.business_details?.contact;
  const isISR = account.holiday_calendar === HolidayCalendar.ISR;

  return (
    <Grid
      container
      flexDirection="column"
      gap={isSmallScreen ? 2 : 3}
      alignItems="center"
      minHeight={isSmallScreen ? '90dvh' : '430px'}
      justifyContent={isSmallScreen ? 'flex-start' : 'center'}
      pt={isSmallScreen ? 2 : 0}
      px={isSmallScreen ? 0 : 2}>
      <Grid container justifyContent="flex-start">
        <LanguageSwitcher languages={[Language.EN, Language.HE]} />
      </Grid>

      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
        <AccountLogo
          account={account}
          width={200}
          style={{ maxWidth: isSmallScreen ? '200px' : '180px', maxHeight: '80px', objectFit: 'contain' }}
        />
        <Typography variant="h6" textAlign="center">
          {account.label?.[language as 'en' | 'he'] || account.name}
        </Typography>
      </Box>

      <Grid container flexDirection="column" alignItems="center" gap={1} maxWidth={420}>
        <Typography variant="h6" fontWeight={600} textAlign="center">
          {t('manage_passes.studioManaged.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t('manage_passes.studioManaged.body')}
        </Typography>
      </Grid>

      {(contact?.email || contact?.phone) && (
        <Grid container flexDirection="column" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('manage_passes.studioManaged.contactPrompt')}
          </Typography>
          {contact?.email && (
            <Grid container alignItems="center" justifyContent="center" gap={1} width="auto">
              <Email sx={{ fontSize: 18, color: 'primary.main' }} />
              <Link href={`mailto:${contact.email}`} sx={{ color: 'text.secondary', textDecoration: 'none' }}>
                <Typography variant="body2">{contact.email}</Typography>
              </Link>
            </Grid>
          )}
          {contact?.phone && (
            <Grid container alignItems="center" justifyContent="center" gap={1} width="auto" dir="ltr">
              {isISR ? (
                <img src="/images/whatsapp.png" alt="WhatsApp" style={{ width: 18, height: 18 }} />
              ) : (
                <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
              )}
              <Link
                href={isISR ? whatsAppLinkFormatter(contact.phone, '') : `tel:${contact.phone}`}
                target={isISR ? '_blank' : undefined}
                rel={isISR ? 'noopener noreferrer' : undefined}
                sx={{ color: 'text.secondary', textDecoration: 'none' }}>
                <Typography variant="body2">{contact.phone}</Typography>
              </Link>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
};
